import { useState } from "react"
import { Card, Button, Badge, Table, Tabs, Alert, Space, Typography, Tag } from "antd"
import {
  AimOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons"
import { TrendingDown, TrendingUp } from "lucide-react"
import Highcharts from 'highcharts'
import HighchartsReact from "highcharts-react-official"
const { Title, Text } = Typography
const { TabPane } = Tabs

const Execution = () => {
  const [selectedTab, setSelectedTab] = useState("executions")

  const manualImpact = 325300;
  const autoImpact = 214000;
  const missedImpact = 608000;

  const options = {
    chart: {
      type: "pie",
      height: 300,
      backgroundColor: "transparent",
    },
    title: {
      text: "Financial Impact Distribution",
      style: {
        fontSize: "18px",
        fontWeight: "600",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      },
    },
    tooltip: {
      pointFormat:
        "<b>{point.percentage:.1f}%</b><br/>Impact: <b>${point.y:,.0f}</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b><br/>{point.percentage:.1f}%",
          style: {
            fontSize: "12px",
          },
        },
        showInLegend: true,
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    series: [
      {
        name: "Impact",
        colorByPoint: true,
        data: [
          {
            name: "Manual Execution",
            y: manualImpact,
            color: "#10b981",
          },
          {
            name: "Auto-Executed",
            y: autoImpact,
            color: "#3b82f6",
          },
          {
            name: "Missed Execution",
            y: missedImpact,
            color: "#ef4444",
          },
        ],
      },
    ],
  };

  // Table data
  const executionsData = [
    {
      key: "1",
      action: "Supply Chain Diversification",
      confidenceSocre: '91/100',
      impact: "+$340K",
      confidence: "91%",
      window: "14 days",
    },
    {
      key: "2",
      action: "Pricing Strategy Pivot",
      confidenceSocre: '54/100',
      impact: "+$180K",
      confidence: "54%",
      window: "7 days",
    },
    {
      key: "3",
      action: "Compliance Automation",
      confidenceSocre: '97/100',
      impact: "+$350K",
      confidence: "97%",
      window: "21 days",
    },
  ]

  const autoExecutionsData = [
    {
      key: "1",
      action: "Currency Hedge Activation",
      impact: "+$125K",
      confidenceScore: "91/100",
      confidence: '91%',
      executionSpeed: "Instant",
    },
    {
      key: "2",
      action: "Inventory Rebalancing",
      impact: "+$89K",
      confidenceScore: "45/100",
      confidence: '45%',
      executionSpeed: "delayed",
    },
  ]

  const missedOpportunitiesData = [
    {
      key: "1",
      opportunity: "LatAm GTM Signal Missed ",
      lost: '-$480,000',
      confidenceScore: "81/100",
      confidence: "87%",
      timeSinceSignal: "15 days",
    },
  ]

  const executionsColumns = [
    {
      title: "Action Taken",
      dataIndex: "action",
      key: "action",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "$ Impact",
      dataIndex: "impact",
      key: "impact",
      render: (text) => <Text style={{ color: "#52c41a", fontWeight: "bold" }}>{text}</Text>,
    },
    {
      title: "Confidence Score",
      dataIndex: "confidenceSocre",
      // key: "confidenceSocre",
    },
    {
      title: "Confidence",
      dataIndex: "confidence",
      key: "confidence",
      render: (text) => <Badge color="blue" text={text} />,
    },
    {
      title: "Time to Execution",
      dataIndex: "window",
      key: "window",
    },
  ]

  const autoExecutionsColumns = [
    {
      title: "Auto-Action",
      dataIndex: "action",
      key: "action",
      render: (text) => (
        <Space>
          <ThunderboltOutlined style={{ color: "#1890ff" }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "$ Impact",
      dataIndex: "impact",
      key: "impact",
      render: (text) => <Text style={{ color: "#52c41a", fontWeight: "bold" }}>{text}</Text>,
    },
    {
      title: "Confidence Score",
      dataIndex: "confidenceScore",
      key: "confidenceScore",
    },
    {
      title: "Confidence",
      dataIndex: "confidence",
      key: "confidence",
    },
    {
      title: "Execution Time",
      dataIndex: "executionSpeed",
      key: "executionSpeed",
    },
  ]

  const missedOpportunitiesColumns = [
    {
      title: "Title",
      dataIndex: "opportunity",
      key: "opportunity",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Est. Loss",
      dataIndex: "loss",
      key: "loss",
      render: (text) => <Text style={{ color: "#ff4d4f", fontWeight: "bold" }}>{text}</Text>,
    },
    {
      title: "Est. Lost",
      dataIndex: "lost",
      key: "lost",
    },
    {
      title: "Confidence Score",
      dataIndex: "confidenceScore",
      key: "confidenceScore",
    },
    {
      title: "Confidence",
      dataIndex: "confidence",
      key: "confidence",
    },
    {
      title: "Time Since Signal",
      dataIndex: "timeSinceSignal",
      key: "timeSinceSignal",
    },
  ]

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <Card style={{ marginBottom: "24px" }}>
      <Title level={4}>
        <DollarOutlined style={{ marginRight: "8px" }} />
        Execution-Adjusted Financial Impact
      </Title>
      <Text type="secondary">
        What your team did or what Radar auto-did — and how much money it made or saved you.
      </Text>

      <Tabs activeKey={selectedTab} onChange={setSelectedTab} style={{ marginTop: "16px" }}>
        <TabPane tab="Summary" key="summary">
          <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="space-y-2">
              <Title level={1} style={{ margin: 0, fontWeight: 700 }}>
                Radar+ Financial Overview
              </Title>
              <Text type="secondary" style={{ fontSize: "16px" }}>
                Quick overview of how Radar+ and your team's actions (or inaction) have affected your financial outcomes.
              </Text>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Manual Execution Impact */}
              <Card
                size="small"
                title="Manual Execution Impact"
                extra={<TrendingUp style={{ color: "#16a34a", fontSize: "16px" }} />}
                style={{ height: "100%" }}
              >
                <div className="text-2xl font-bold text-green-600">+{formatCurrency(325300)}</div>
              </Card>

              {/* Auto-Execution Impact */}
              <Card
                size="small"
                title="Auto-Execution Impact"
                extra={<ThunderboltOutlined style={{ color: "#2563eb", fontSize: "16px" }} />}
                style={{ height: "100%" }}
              >
                <div className="text-2xl font-bold text-blue-600">+{formatCurrency(214000)}</div>
              </Card>

              {/* Missed Execution Loss */}
              <Card
                size="small"
                title="Missed Execution Loss"
                extra={<TrendingDown style={{ color: "#dc2626", fontSize: "16px" }} />}
                style={{ height: "100%" }}
              >
                <div className="text-2xl font-bold text-red-600">–{formatCurrency(608000)}</div>
              </Card>

              {/* Top Performing Action */}
              <Card
                size="small"
                title="Top Performing Action"
                extra={<AimOutlined style={{ color: "#16a34a", fontSize: "16px" }} />}
                style={{ height: "100%" }}
              >
                <div className="space-y-1">
                  <div className="text-sm font-medium">Vendor Cost Spike</div>
                  <div className="text-lg font-bold text-green-600">{formatCurrency(44800)} gain</div>
                </div>
              </Card>

              {/* Fastest Execution */}
              <Card
                size="small"
                title="Fastest Execution"
                extra={<ClockCircleOutlined style={{ color: "#2563eb", fontSize: "16px" }} />}
                style={{ height: "100%" }}
              >
                <div className="space-y-1">
                  <div className="text-sm font-medium">Invoicing Fix</div>
                  <div className="text-lg font-bold text-blue-600">1 day</div>
                </div>
              </Card>

              {/* Suggested Rule */}
              <Card
                size="small"
                title="Suggested Rule"
                extra={<SettingOutlined style={{ color: "#9333ea", fontSize: "16px" }} />}
                style={{ height: "100%" }}
              >
                <div className="space-y-2">
                  <Tag color="purple">Auto-execute if $ Impact {">"} $100K</Tag>
                </div>
              </Card>
            </div>

            {/* Chart Section */}
            <Card title="Financial Impact Breakdown">
              <HighchartsReact highcharts={Highcharts} options={options} />
            </Card>

            {/* Summary Stats */}
            <Card
              title={
                <Title level={3} style={{ margin: 0 }}>
                  Summary
                </Title>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="space-y-2">
                  <Title level={2} style={{ margin: 0, color: "#16a34a" }}>
                    +{formatCurrency(539300)}
                  </Title>
                  <Text type="secondary">Total Positive Impact</Text>
                </div>
                <div className="space-y-2">
                  <Title level={2} style={{ margin: 0, color: "#dc2626" }}>
                    –{formatCurrency(608000)}
                  </Title>
                  <Text type="secondary">Total Missed Opportunities</Text>
                </div>
                <div className="space-y-2">
                  <Title level={2} style={{ margin: 0, color: "#6b7280" }}>
                    –{formatCurrency(68700)}
                  </Title>
                  <Text type="secondary">Net Impact</Text>
                </div>
              </div>
            </Card>
          </div>
        </TabPane>
        <TabPane tab="Executions" key="executions">
          <Table dataSource={executionsData} columns={executionsColumns} pagination={false} size="middle" />
        </TabPane>
        <TabPane tab="Auto-Executions" key="auto-executions">
          <Table dataSource={autoExecutionsData} columns={autoExecutionsColumns} pagination={false} size="middle" />
        </TabPane>
        <TabPane tab="Missed Executions" key="missed">
          <Table
            dataSource={missedOpportunitiesData}
            columns={missedOpportunitiesColumns}
            pagination={false}
            size="middle"
          />
        </TabPane>
      </Tabs>
    </Card>
  )
}

export default Execution